import React, { useState, useEffect, forwardRef } from 'react'
import { isEmpty, isString } from 'lodash'
import { Controller } from 'react-hook-form'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  Grid,
  Tooltip,
  TextField,
  Button,
  makeStyles,
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'

import { Collapse as CollapseComponent } from 'components'
import { DataProcessSourcesTable } from 'views/DataProcesses/components/'

import useDataProcess from 'hooks/useDataProcess'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import helpers from 'helpers'
import constants from 'constants/index'
import styles from '../../../New/styles'

const useStyles = makeStyles(styles)

const Collapse = forwardRef(
  (
    {
      departments,
      dataSource,
      index,
      sourceLabelOptions,
      allSources,
      handleSourceDataSelected,
      handleOpenedOtherSourceAdd,
      setAllSources,
      setValue,
      errors,
      control,
      isEdit,
      lastOne,
    },
    ref,
  ) => {
    const validErrorSource = () => {
      const typeValid =
        helpers.dataProcess.convertSourceDescriptionToSource(dataSource) ===
          constants.dataProcess.THIRD_PARTY_SOURCE ||
        constants.dataProcess.PUBLIC_SOURCE ||
        constants.dataProcess.ANOTHER_SOURCE

      if (isEmpty(dataSource?.dataProcessSources) && typeValid) {
        return true
      } else {
        return false
      }
    }

    const snackbar = useSnackbar()
    const { data, dataProcessSources } = useDataProcess()
    const classes = useStyles()

    const [sourceOptions, setSourceOptions] = useState([])
    const [destroying, setDestroying] = useState(false)
    const [selectedValue, setSelectedValue] = useState(null)

    const removeOption = (entity) => {
      const currentData = allSources[index]
      const currentSources = currentData?.dataProcessSources

      const dataProcessSources = currentSources.filter(
        (source) => source.entityId !== entity.entityId,
      )

      const newDataSources = allSources.filter(
        (source) => source.sourceDescription !== dataSource.sourceDescription,
      )

      const dataSources = helpers.functions.insertArrayAt(
        newDataSources,
        index,
        {
          sourceDescription: dataSource.sourceDescription,
          dataProcessSources,
        },
      )

      setAllSources(dataSources)
      setValue('dataProcessDataSources', dataSources)
    }

    const handleAddAllDepartments = () => {
      const currentData = allSources[index]
      const currentSources = currentData?.dataProcessSources || []

      const newDepartments = departments.filter(
        (department) =>
          !currentSources.some((source) => source.entityId === department.id),
      )

      const mappedDepartments = newDepartments.map((department) => ({
        entityId: department.id,
        entityName: department.name,
      }))

      const updatedSources = [...currentSources, ...mappedDepartments]
      const updatedAllSources = allSources.map((currentSource, currentIndex) =>
        currentIndex === index
          ? {
              ...currentSource,
              dataProcessSources: updatedSources,
            }
          : currentSource,
      )

      setAllSources(updatedAllSources)
      setValue('dataProcessDataSources', updatedAllSources)
    }

    const shouldAddAllDepartments = () => {
      if (
        selectedValue?.entityId === constants.dataProcess.ADD_ALL_DEPARTMENTS
      ) {
        handleAddAllDepartments()
      } else {
        handleSourceDataSelected(index)
      }
    }

    const removeSource = async () => {
      if (destroying) {
        return
      }

      if (isEdit) {
        setDestroying(true)
        try {
          await service.dponet.dataProcesses.destroyDataProcessDataSource({
            dataProcessId: data?.dataProcess?.id,
            dataProcessDataSourceId: dataSource.id,
            lastOne,
            mustHaveLeastOneRecord: false,
          })
          snackbar.open({
            message: 'Origem dos dados removida com sucesso',
            variant: 'success',
          })
          setDestroying(false)
        } catch (error) {
          snackbar.open({
            message: helpers.formatters.errorMessage(
              error?.response?.data?.error,
            ),
            variant: 'error',
          })
          return setDestroying(false)
        }
      }

      const newDataSources = allSources.filter(
        (source) => source.sourceDescription !== dataSource.sourceDescription,
      )

      setValue('dataProcessDataSources', newDataSources)
      setAllSources(newDataSources)
    }

    const isVisible = constants.dataProcess.SOURCES_WITH_ENTITY.includes(
      dataSource.sourceDescription,
    )

    const convertedSourceDescription =
      typeof dataSource.sourceDescription === 'string'
        ? helpers.dataProcess.convertSourceDescriptionToSource(
            dataSource.sourceDescription,
          )
        : dataSource.sourceDescription

    const defaultOpen = allSources.length - 1 === index && !dataSource.id

    useEffect(() => {
      if (defaultOpen && ref.current) {
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
      //eslint-disable-next-line
    }, [])

    useEffect(() => {
      if (isVisible && dataProcessSources) {
        const source = isString(dataSource.sourceDescription)
          ? helpers.dataProcess.convertSourceDescriptionToSource(
              dataSource.sourceDescription,
            )
          : dataSource.sourceDescription

        const formattedSources = helpers.dataProcess.mountDataProcessSources(
          dataProcessSources,
          departments,
          source,
        )
        setSourceOptions(formattedSources)
      }
      //eslint-disable-next-line
    }, [])

    return (
      <CollapseComponent
        title={
          <Typography ref={ref}>
            {sourceLabelOptions.find(
              (source) => source.id === dataSource.sourceDescription,
            )?.name || dataSource.sourceDescription}
          </Typography>
        }
        defaultOpen={defaultOpen && isVisible}
        visibilityChildren={isVisible}
        onRemove={removeSource}
      >
        <Grid container>
          <Grid className={classes.otherInputSource} item xs={12}>
            <Box width="100%">
              <Box my={1}>
                <Tooltip
                  noWrap
                  title={helpers.dataProcess.labelSource(
                    dataSource.sourceDescription,
                  )}
                  placement="bottom-start"
                >
                  <Typography variant="body2" color="textSecondary">
                    {helpers.dataProcess.labelSource(
                      dataSource.sourceDescription,
                    )}
                  </Typography>
                </Tooltip>
              </Box>
              <Box
                display="flex"
                justifyContent="space-evenly"
                alignItems="center"
              >
                <Box width="100%" mr={3}>
                  <Controller
                    as={
                      <Autocomplete
                        options={sourceOptions}
                        getOptionLabel={(option) => option.entityName}
                        classes={{
                          inputRoot: classes.autoComplete,
                        }}
                        getOptionSelected={(option, value) =>
                          option.entityId === value.entityId ||
                          option.entityId === value.entityName
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            helperText={
                              validErrorSource() && 'Selecione uma das opções'
                            }
                            error={validErrorSource()}
                            variant="outlined"
                          />
                        )}
                      />
                    }
                    control={control}
                    rules={{ required: true }}
                    onChange={([, data]) => {
                      setSelectedValue(data)
                      return data
                    }}
                    mode="onChange"
                    name={`${constants.dataProcess.DATA_PROCESS_SOURCES_FIELD}-${index}`}
                    defaultValue={null}
                  />
                </Box>
                <Button
                  onClick={shouldAddAllDepartments}
                  color="primary"
                  variant="contained"
                >
                  Adicionar
                </Button>
              </Box>
            </Box>
            {!isEmpty(dataSource.dataProcessSources) && (
              <Box pt={2} width="100%">
                <Typography variant="body2" color="textSecondary">
                  {helpers.dataProcess.labelDataProcessSourcesTable([
                    helpers.dataProcess.convertSourceKind(
                      convertedSourceDescription,
                    ),
                  ])}
                </Typography>
                <DataProcessSourcesTable
                  dataProcessSources={dataSource.dataProcessSources}
                  removeOption={removeOption}
                  type={helpers.dataProcess.convertSourceKind(
                    convertedSourceDescription,
                  )}
                />
              </Box>
            )}

            {dataSource.sourceDescription !==
              constants.dataProcess.DEPARTMENT_SOURCE &&
              dataSource.sourceDescription !==
                constants.dataProcess.DEPARTMENT_DESCRIPTION && (
                <Box mt={1}>
                  <Typography variant="body2" color="textSecondary">
                    Não encontrou onde os dados são disponibilizados?{' '}
                    <span
                      onClick={() => handleOpenedOtherSourceAdd(index)}
                      className={classes.otherSource}
                    >
                      Clique aqui
                    </span>{' '}
                    para adicionar
                  </Typography>
                </Box>
              )}
            <Box mt={2} width="100%" display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="secondary"
                className={classes.redBackground}
                onClick={removeSource}
              >
                Remover
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CollapseComponent>
    )
  },
)

Collapse.propTypes = {
  dataSource: PropTypes.object.isRequired,
  index: PropTypes.number,
  sourceLabelOptions: PropTypes.array,
  allSources: PropTypes.array,
  handleSourceDataSelected: PropTypes.func,
  handleOpenedOtherSourceAdd: PropTypes.func,
  setAllSources: PropTypes.func,
  setValue: PropTypes.func,
  errors: PropTypes.object,
  control: PropTypes.object,
  isEdit: PropTypes.bool,
  lastOne: PropTypes.bool,
}

export default Collapse
